import { useState, useEffect } from 'react';
import {
  getCountdownDisplay,
  getCountdownSegments,
  type CountdownSegments,
} from '../helpers/countdownToDate.helper';
import { COUNTDOWN_STARTED } from '../constants/challenges.constants';

const MINUTE_MS = 60_000;

export interface CountdownStatus {
  display: string;
  isUrgent: boolean;
  segments: CountdownSegments | null;
}

/**
 * "Minute heartbeat" hook: returns { display, isUrgent, segments } for a countdown to targetDateIso.
 * segments: zero-padded { days, hours, minutes } for segmented UI, or null when started/invalid.
 * Updates every 60 s; recalculates from Date.now() each tick (no clock drift).
 */
export const useCountdownToDate = (targetDateIso: string | null): CountdownStatus => {
  const [status, setStatus] = useState<CountdownStatus>({
    display: '',
    isUrgent: false,
    segments: null,
  });

  useEffect(() => {
    if (!targetDateIso) {
      setStatus({ display: '', isUrgent: false, segments: null });
      return;
    }

    const update = () => {
      const target = new Date(targetDateIso);
      const now = new Date();
      const diffMs = target.getTime() - now.getTime();

      const display = getCountdownDisplay(target);
      const segments = getCountdownSegments(target);
      const isUrgent = diffMs > 0 && diffMs < 3_600_000;

      setStatus({ display, isUrgent, segments });
      return display === COUNTDOWN_STARTED;
    };

    const isDone = update();
    if (isDone) return;

    const interval = setInterval(() => {
      if (update()) clearInterval(interval);
    }, MINUTE_MS);

    return () => clearInterval(interval);
  }, [targetDateIso]);

  return status;
};
